import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/integrations';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  Plug,
  Link as LinkIcon,
  Unlink,
  CheckCircle2,
  Eye,
  EyeOff,
  RefreshCw,
  Loader2,
  Users,
  Stethoscope,
  Calendar,
  Clock,
  Settings,
  AlertTriangle,
  Activity,
} from 'lucide-react';
import { INTEGRATION_CATALOG, getCatalogEntry, type IntegrationCatalogEntry } from '@vagusx/shared';
import { SYNC_STATUS, CATALOG_IMPORT_SOURCES, ehrErrorMessage } from '@vagusx/clients';
import { getIntegrationClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ConnectorActivitySheet } from './ConnectorActivitySheet';
import { StreamSyncDialog } from './StreamSyncDialog';
import type { IntegrationResponse, SyncOptions } from '@vagusx/clients';

// =============================================================================
// Constants
// =============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  email: 'Email',
  ehr: 'EHR Systems',
  practice_management: 'Practice Management',
  telephony: 'Telephony',
};

// =============================================================================
// Main Page
// =============================================================================

export default function IntegrationsPage(): React.ReactElement {
  const { oauth, message } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const oauthHandled = useRef(false);
  const [configDrawer, setConfigDrawer] = useState<{
    entry: IntegrationCatalogEntry;
    integration: IntegrationResponse | null;
  } | null>(null);
  const [disconnectIntegration, setDisconnectIntegration] = useState<IntegrationResponse | null>(
    null
  );
  const [syncIntegration, setSyncIntegration] = useState<{
    integration: IntegrationResponse;
    displayName: string;
  } | null>(null);
  const [streamSyncIntegration, setStreamSyncIntegration] = useState<{
    integration: IntegrationResponse;
    displayName: string;
  } | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);

  const {
    data: integrations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.integrations.list(),
    queryFn: () => getIntegrationClient().getAll(),
    staleTime: 30 * 1000,
  });

  // Handle OAuth redirect result
  useEffect(() => {
    if (oauth && !oauthHandled.current) {
      oauthHandled.current = true;
      if (oauth === 'success') {
        queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
        toast.success('Integration connected successfully');
      } else {
        toast.error('Failed to connect integration', {
          description: message || 'Unknown error',
        });
      }
      navigate({ to: '/setup/integrations', search: {}, replace: true });
    }
  }, [oauth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a map of connected integrations by source type
  const connectedBySource = useMemo(() => {
    const map = new Map<string, IntegrationResponse>();
    for (const integration of integrations) {
      map.set(integration.source, integration);
    }
    return map;
  }, [integrations]);

  // Split catalog into connected and available
  const { connectedEntries, availableEntries } = useMemo(() => {
    const connected: IntegrationCatalogEntry[] = [];
    const available: IntegrationCatalogEntry[] = [];
    for (const entry of INTEGRATION_CATALOG) {
      if (connectedBySource.has(entry.type)) {
        connected.push(entry);
      } else {
        available.push(entry);
      }
    }
    return { connectedEntries: connected, availableEntries: available };
  }, [connectedBySource]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Integrations</h2>
          <p className="text-sm text-muted-foreground">
            Connect your systems to sync data automatically
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setActivityOpen(true)}
        >
          <Activity className="h-4 w-4" />
          EHR activity
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">Failed to load integrations.</p>}

      {/* Connected integrations */}
      {connectedEntries.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Connected
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {connectedEntries.map((entry) => (
              <IntegrationCard
                key={entry.type}
                entry={entry}
                integration={connectedBySource.get(entry.type) ?? null}
                isSyncing={syncingId === (connectedBySource.get(entry.type)?.id ?? '')}
                onConnect={() => handleConnect(entry)}
                onDisconnect={(integration) => setDisconnectIntegration(integration)}
                onOpenConfig={(integration) => setConfigDrawer({ entry, integration })}
                onSync={(integration) => handleSync(entry, integration)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Separator */}
      {connectedEntries.length > 0 && availableEntries.length > 0 && (
        <hr className="border-border" />
      )}

      {/* Available integrations */}
      {availableEntries.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Available
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {availableEntries.map((entry) => (
              <IntegrationCard
                key={entry.type}
                entry={entry}
                integration={null}
                isSyncing={false}
                onConnect={() => handleConnect(entry)}
                onDisconnect={(integration) => setDisconnectIntegration(integration)}
                onOpenConfig={(integration) => setConfigDrawer({ entry, integration })}
                onSync={(integration) => handleSync(entry, integration)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Config Drawer */}
      {configDrawer && (
        <IntegrationConfigDrawer
          open={true}
          onOpenChange={(open) => {
            if (!open) setConfigDrawer(null);
          }}
          entry={configDrawer.entry}
          integration={configDrawer.integration}
          onDisconnect={() => {
            if (configDrawer.integration) {
              setConfigDrawer(null);
              setDisconnectIntegration(configDrawer.integration);
            }
          }}
        />
      )}

      {/* Disconnect Dialog */}
      {disconnectIntegration && (
        <DisconnectDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setDisconnectIntegration(null);
          }}
          integration={disconnectIntegration}
        />
      )}

      {/* Sync Dialog */}
      {syncIntegration && (
        <SyncDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setSyncIntegration(null);
          }}
          integration={syncIntegration.integration}
          displayName={syncIntegration.displayName}
        />
      )}

      {/* Stream Sync Dialog (ADR-299) */}
      {streamSyncIntegration && (
        <StreamSyncDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setStreamSyncIntegration(null);
          }}
          integration={streamSyncIntegration.integration}
          displayName={streamSyncIntegration.displayName}
        />
      )}

      {/* EHR connector-activity panel (ADR-095) */}
      <ConnectorActivitySheet open={activityOpen} onOpenChange={setActivityOpen} />
    </div>
  );

  function handleSync(entry: IntegrationCatalogEntry, integration: IntegrationResponse): void {
    // EHRs whose syncs run as streams (athena): the Sync dialog picks which syncs
    // run now and manages the change-feed subscriptions (ADR-299). Nothing here
    // re-reads slots or appointments in bulk unless the user ticks it.
    if (CATALOG_IMPORT_SOURCES.includes(entry.type)) {
      setStreamSyncIntegration({ integration, displayName: entry.name });
      return;
    }

    if (entry.authType === 'oauth') {
      // Gmail: fire-and-forget, no options dialog needed — card tracks loading via syncingId
      setSyncingId(integration.id);
      getIntegrationClient()
        .sync(integration.id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
          toast.success('Sync started');
        })
        .catch(() => {
          // Global error handler shows toast
        })
        .finally(() => setSyncingId(null));
    } else {
      // Other EHRs (Office Ally): scope picker for the streaming sync.
      setSyncIntegration({ integration, displayName: entry.name });
    }
  }

  function handleConnect(entry: IntegrationCatalogEntry): void {
    if (entry.authType === 'oauth') {
      getIntegrationClient()
        .getOAuthUrl(entry.type)
        .then(({ url }) => {
          window.location.href = url;
        })
        .catch(() => {
          toast.error('Failed to start OAuth flow');
        });
    } else {
      setConfigDrawer({ entry, integration: null });
    }
  }
}

// =============================================================================
// Integration Card
// =============================================================================

function IntegrationCard({
  entry,
  integration,
  isSyncing,
  onConnect,
  onDisconnect,
  onOpenConfig,
  onSync,
}: {
  entry: IntegrationCatalogEntry;
  integration: IntegrationResponse | null;
  isSyncing: boolean;
  onConnect: () => void;
  onDisconnect: (integration: IntegrationResponse) => void;
  onOpenConfig: (integration: IntegrationResponse) => void;
  onSync: (integration: IntegrationResponse) => void;
}): React.ReactElement {
  // The card surfaces six states derived from two server fields:
  //   enabled ∈ {true, false}  and  syncStatus ∈ {IDLE, SYNCING, FAILED, AUTH_FAILED}
  //
  //   not_connected    → integration === null           → [Connect]
  //   healthy          → enabled && syncStatus ≠ FAILED → [Sync] [Disconnect] + green check
  //   transient_failed → enabled && syncStatus = FAILED → [Sync] [Disconnect] + warning (still retrying)
  //   syncing          → enabled && syncStatus = SYNCING→ [Syncing…] [Disconnect]
  //   auth_broken      → syncStatus = AUTH_FAILED       → [Reconnect] + syncError on hover
  //   user_disabled    → !enabled && syncStatus ≠ AUTH_FAILED → [Connect] (label matches fresh install)
  //
  // AUTH_FAILED is the terminal "reconnect required" state — it always
  // comes with enabled=false (server flips it atomically). We key on
  // syncStatus rather than enabled here so the Reconnect button still
  // shows even after the user reconnects in another tab (AUTH_FAILED
  // hasn't been cleared yet locally).
  const isAuthBroken = integration !== null && integration.syncStatus === SYNC_STATUS.AUTH_FAILED;
  const isConnected = integration !== null && integration.enabled && !isAuthBroken;
  // Keep isTransientFailed independent of isConnected — if someone
  // reshuffles the state derivation, this invariant is self-contained:
  // "integration is enabled and its last sync failed transiently".
  const isTransientFailed =
    integration !== null && integration.enabled && integration.syncStatus === SYNC_STATUS.FAILED;
  // Auth is failing but the integration is still ENABLED — the EHR-connector posture, which
  // is alert-only: nothing goes dark without a human (ADR-231). Distinct from `isAuthBroken`
  // (OAuth sources), which is terminal AND disabled. Set by the worker the moment a refresh
  // fails with an AUTH_* code, cleared by the next successful one, so this can't latch.
  const isAuthWarning =
    integration !== null &&
    integration.enabled &&
    !isAuthBroken &&
    integration.config?.authAlertActive === true;
  const authWarningCode =
    typeof integration?.config?.lastAuthErrorCode === 'string'
      ? integration.config.lastAuthErrorCode
      : null;
  // The EHR account this integration reached (athena: the practice name), stored by the
  // API on save for connectors that can name it (ADR-260).
  const accountName =
    typeof integration?.config?.accountName === 'string' ? integration.config.accountName : null;
  // The standard message for the stored code (ADR-253), the same for every EHR;
  // the stored text only when there is no registered code (e.g. Gmail).
  const syncErrorText = ehrErrorMessage(integration?.syncErrorCode, integration?.syncError);
  const authWarningText =
    ehrErrorMessage(authWarningCode ?? integration?.syncErrorCode, integration?.syncError) ??
    'The EHR rejected our credentials. Retrying will not fix it — reset the password in the EHR portal, then reconnect.';

  return (
    <Card className="group relative flex flex-col">
      {isConnected && !isTransientFailed && (
        <CheckCircle2 className="absolute top-3 right-3 h-4 w-4 text-green-500" />
      )}
      {(isConnected || isAuthBroken) && (
        <button
          onClick={() => onOpenConfig(integration!)}
          className="absolute top-3 left-3 hidden rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground group-hover:block"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>
      )}
      <CardContent className="flex flex-1 flex-col items-center gap-3 pt-6 pb-4">
        {/* Logo */}
        <IntegrationLogo type={entry.type} logoUrl={entry.logoUrl} />

        {/* Name + status */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{entry.name}</span>
          {isConnected && !isTransientFailed && <LinkIcon className="h-3.5 w-3.5 text-green-500" />}
          {(isTransientFailed || isAuthBroken) && (
            <Unlink className="h-3.5 w-3.5 text-destructive" />
          )}
        </div>

        {isConnected && accountName && (
          <p className="max-w-full truncate text-xs text-muted-foreground" title={accountName}>
            {accountName}
          </p>
        )}

        {/* Status text */}
        {isConnected && !isTransientFailed && (
          <div className="text-center">
            <p className="text-xs text-green-600">Connected</p>
            {integration?.lastSyncAt && (
              <p className="text-xs text-muted-foreground">
                Last synced{' '}
                {formatDistanceToNow(new Date(integration.lastSyncAt), { addSuffix: true })}
              </p>
            )}
          </div>
        )}
        {isTransientFailed && <p className="text-xs text-destructive">Sync failed</p>}
        {isAuthBroken &&
          (syncErrorText ? (
            // Wrapping the text — not a wider area — keeps the tooltip
            // scoped and avoids fighting with the Settings button on hover.
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="cursor-help text-xs text-destructive underline decoration-dotted underline-offset-2">
                    Reconnect required
                  </p>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">{syncErrorText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            // No error reason on file — drop the hover affordance so we
            // don't advertise a tooltip that'll render nothing.
            <p className="text-xs text-destructive">Reconnect required</p>
          ))}

        {isAuthWarning && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="cursor-help text-xs font-medium text-amber-700 underline decoration-dotted underline-offset-2">
                  Credentials need attention
                </p>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="text-xs">{authWarningText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Action button */}
        <div className="mt-auto flex w-full flex-col gap-2">
          {/* Non-blocking: the integration stays enabled and Sync/Disconnect stay available
              below. This just makes the fix one click away instead of buried in Settings. */}
          {isAuthWarning && (
            <Button variant="outline" size="sm" className="w-full" onClick={onConnect}>
              Reconnect
            </Button>
          )}
          {isAuthBroken ? (
            <Button variant="outline" size="sm" className="w-full" onClick={onConnect}>
              Reconnect
            </Button>
          ) : isConnected ? (
            <div className="flex w-full gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={isSyncing}
                onClick={() => onSync(integration!)}
              >
                {isSyncing ? (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                )}
                {isSyncing ? 'Syncing...' : 'Sync'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => onDisconnect(integration!)}
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="w-full" onClick={onConnect}>
              Connect
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Integration Logo
// =============================================================================

function IntegrationLogo({
  type,
  logoUrl,
}: {
  type: string;
  logoUrl?: string;
}): React.ReactElement {
  if (logoUrl) {
    return (
      <div className="flex h-16 shrink-0 items-center justify-center rounded-lg p-2">
        <img src={logoUrl} alt={type} className="max-h-12 w-auto object-contain" />
      </div>
    );
  }

  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-muted">
      <Plug className="h-8 w-8 text-muted-foreground" />
    </div>
  );
}

// =============================================================================
// Integration Config Drawer
// =============================================================================

function IntegrationConfigDrawer({
  open,
  onOpenChange,
  entry,
  integration,
  onDisconnect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: IntegrationCatalogEntry;
  integration: IntegrationResponse | null;
  onDisconnect: () => void;
}): React.ReactElement {
  if (entry.authType === 'oauth' && integration) {
    return (
      <GmailConfigDrawer
        open={open}
        onOpenChange={onOpenChange}
        entry={entry}
        integration={integration}
        onDisconnect={onDisconnect}
      />
    );
  }

  if (entry.authType === 'oauth_client_credentials') {
    return (
      <OAuthClientCredsConfigDrawer
        open={open}
        onOpenChange={onOpenChange}
        entry={entry}
        integration={integration}
        onDisconnect={onDisconnect}
      />
    );
  }

  return (
    <CredentialConfigDrawer
      open={open}
      onOpenChange={onOpenChange}
      entry={entry}
      integration={integration}
      onDisconnect={onDisconnect}
    />
  );
}

// =============================================================================
// Gmail Config Drawer
// =============================================================================

function getWatchStatus(config: Record<string, unknown>): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive';
} {
  const expiresAt = config.watchExpiresAt ? new Date(config.watchExpiresAt as string) : null;

  if (!expiresAt) {
    return { label: 'Not set', variant: 'secondary' };
  }

  const now = new Date();
  const twoDays = 2 * 24 * 60 * 60 * 1000;

  if (expiresAt < now) {
    return { label: 'Expired', variant: 'destructive' };
  }

  if (expiresAt.getTime() - now.getTime() < twoDays) {
    return { label: 'Expiring soon', variant: 'secondary' };
  }

  return { label: 'Active', variant: 'default' };
}

function GmailConfigDrawer({
  open,
  onOpenChange,
  entry,
  integration,
  onDisconnect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: IntegrationCatalogEntry;
  integration: IntegrationResponse;
  onDisconnect: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const config = integration.config ?? {};
  const creds = integration.credentials ?? {};
  const watchStatus = getWatchStatus(config);

  // Blacklist state — parse from array to comma-separated string
  const existingBlacklist = Array.isArray(config.blacklist) ? (config.blacklist as string[]) : [];
  const [blacklistText, setBlacklistText] = useState(existingBlacklist.join(', '));

  // Subject blacklist — comma-separated substrings, matched case-insensitively
  // against the email Subject header. Independent of the sender blacklist; an
  // email is dropped if either filter hits.
  const existingSubjectBlacklist = Array.isArray(config.subjectBlacklist)
    ? (config.subjectBlacklist as string[])
    : [];
  const [subjectBlacklistText, setSubjectBlacklistText] = useState(
    existingSubjectBlacklist.join(', ')
  );

  // Live-parsed entry counts shown beneath each textarea so users can see
  // exactly what would be saved (commas / whitespace silently drop, which
  // was opaque without this).
  const blacklistEntryCount = useMemo(
    () =>
      blacklistText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length,
    [blacklistText]
  );
  const subjectBlacklistEntryCount = useMemo(
    () =>
      subjectBlacklistText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length,
    [subjectBlacklistText]
  );

  // Sync labels state
  const GMAIL_LABEL_OPTIONS = [
    { value: 'INBOX', label: 'Inbox' },
    { value: 'SENT', label: 'Sent' },
    { value: 'SPAM', label: 'Spam' },
    { value: 'TRASH', label: 'Trash' },
  ];
  const existingSyncLabels = Array.isArray(config.syncLabels)
    ? (config.syncLabels as string[])
    : ['INBOX'];
  const [syncLabels, setSyncLabels] = useState<string[]>(existingSyncLabels);

  const saveMutation = useMutation({
    mutationFn: () => {
      const entries = blacklistText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const subjectEntries = subjectBlacklistText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return getIntegrationClient().update(integration.id, {
        config: { blacklist: entries, subjectBlacklist: subjectEntries, syncLabels },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success('Settings saved');
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => getIntegrationClient().sync(integration.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success('Sync started');
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Wider than the other drawers: its footer holds four actions on one row. */}
      <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
        <SheetHeader className="border-b">
          <div className="flex items-center gap-3">
            {entry.logoUrl ? (
              <img src={entry.logoUrl} alt={entry.type} className="h-8 w-8 object-contain" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
                <Plug className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div>
              <SheetTitle>{entry.name}</SheetTitle>
              <SheetDescription className="mt-0.5">{entry.description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <SheetBody className="space-y-6 py-6">
          {/* Connected account */}
          <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{(creds.email as string) ?? 'Unknown'}</span>
              <Badge variant={watchStatus.variant}>{watchStatus.label}</Badge>
            </div>

            <div className="space-y-1.5">
              {typeof config.watchExpiresAt === 'string' && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Watch expires</span>
                  <span>
                    {formatDistanceToNow(new Date(config.watchExpiresAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Last synced</span>
                <span>
                  {integration.lastSyncAt
                    ? formatDistanceToNow(new Date(integration.lastSyncAt), { addSuffix: true })
                    : 'Never'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Status</span>
                {integration.syncStatus === SYNC_STATUS.IDLE && (
                  <span className="text-muted-foreground">Idle</span>
                )}
                {integration.syncStatus === SYNC_STATUS.SYNCING && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Syncing
                  </span>
                )}
                {integration.syncStatus === SYNC_STATUS.FAILED && (
                  <span className="flex items-center gap-1 text-destructive">
                    <AlertTriangle className="h-3 w-3" /> Failed
                  </span>
                )}
                {integration.syncStatus === SYNC_STATUS.AUTH_FAILED && (
                  <span className="flex items-center gap-1 text-destructive">
                    <Unlink className="h-3 w-3" /> Reconnect required
                  </span>
                )}
              </div>
            </div>

            {integration.syncError && (
              <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {ehrErrorMessage(integration.syncErrorCode, integration.syncError)}
              </div>
            )}
          </div>

          {/* Sync Labels Section */}
          <div className="space-y-2">
            <Label>Sync labels</Label>
            <p className="text-xs text-muted-foreground">
              Only emails with these Gmail labels will be synced.
            </p>
            <div className="space-y-2">
              {GMAIL_LABEL_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={syncLabels.includes(opt.value)}
                    onCheckedChange={(checked) => {
                      setSyncLabels((prev) => {
                        const next = checked
                          ? [...prev, opt.value]
                          : prev.filter((l) => l !== opt.value);
                        return next.length === 0 ? ['INBOX'] : next;
                      });
                    }}
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Blacklist Section */}
          <div className="space-y-2">
            <Label htmlFor="blacklist">Blocked senders</Label>
            <p className="text-xs text-muted-foreground">
              Emails from these addresses and domains will be skipped during sync. Separate with
              commas.
            </p>
            <Textarea
              id="blacklist"
              value={blacklistText}
              onChange={(e) => setBlacklistText(e.target.value)}
              placeholder="noreply@company.com, marketing.com"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {blacklistEntryCount} {blacklistEntryCount === 1 ? 'entry' : 'entries'}
            </p>
          </div>

          {/* Subject Blacklist Section */}
          <div className="space-y-2">
            <Label htmlFor="subject-blacklist">Blocked subjects</Label>
            <p className="text-xs text-muted-foreground">
              Emails whose subject contains any of these phrases will be skipped during sync.
              Case-insensitive substring match. Separate with commas.
            </p>
            <Textarea
              id="subject-blacklist"
              value={subjectBlacklistText}
              onChange={(e) => setSubjectBlacklistText(e.target.value)}
              placeholder="[Newsletter], Out of office:, unsubscribe"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {subjectBlacklistEntryCount} {subjectBlacklistEntryCount === 1 ? 'entry' : 'entries'}
            </p>
          </div>
        </SheetBody>

        <SheetActions
          start={
            <>
              <Button variant="outline" onClick={onDisconnect}>
                Disconnect
              </Button>
              <Button
                variant="outline"
                disabled={syncMutation.isPending || integration.syncStatus === 1}
                onClick={() => syncMutation.mutate()}
              >
                {syncMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                )}
                Sync Now
              </Button>
            </>
          }
        >
          <SheetCancel />
          <SaveButton
            type="button"
            onClick={() => saveMutation.mutate()}
            isPending={saveMutation.isPending}
          />
        </SheetActions>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// Credential Config Drawer (OfficeAlly, etc.)
// =============================================================================

function CredentialConfigDrawer({
  open,
  onOpenChange,
  entry,
  integration,
  onDisconnect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: IntegrationCatalogEntry;
  integration: IntegrationResponse | null;
  onDisconnect: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const isCreateMode = integration === null;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [configFields, setConfigFields] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of entry.fields) {
      initial[field.key] = '';
    }
    return initial;
  });

  // Populate the form from the stored integration when the drawer opens (edit mode).
  // Username lives in plaintext `credentials`; password is encrypted and never returned.
  useEffect(() => {
    if (!open) return;
    const creds = (integration?.credentials ?? {}) as Record<string, unknown>;
    setUsername(typeof creds.username === 'string' ? creds.username : '');
    setPassword('');
    const nextConfig: Record<string, string> = {};
    for (const field of entry.fields) {
      const v = creds[field.key];
      nextConfig[field.key] = typeof v === 'string' ? v : '';
    }
    setConfigFields(nextConfig);
  }, [open, integration, entry.fields]);

  const testMutation = useMutation({
    mutationFn: () =>
      getIntegrationClient().testCredentials({
        source: entry.type,
        credentials: { username, password },
      }),
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(configFields)) {
        if (value) config[key] = value;
      }
      return getIntegrationClient().create({
        source: entry.type,
        credentials: { username, password },
        config,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success(`${entry.name} connected`);
      onOpenChange(false);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(configFields)) {
        if (value) config[key] = value;
      }
      // Only send password if user typed a new one; empty means "keep existing".
      const credentials: Record<string, string> = { username };
      if (password.length > 0) credentials.password = password;
      return getIntegrationClient().update(integration!.id, {
        credentials,
        config,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success(`${entry.name} updated`);
    },
  });

  const canTest = username.length > 0 && password.length > 0;
  const canCreate = username.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <div className="flex items-center gap-3">
            {entry.logoUrl ? (
              <img src={entry.logoUrl} alt={entry.type} className="h-8 w-8 object-contain" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
                <Plug className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div>
              <SheetTitle>{isCreateMode ? `Connect ${entry.name}` : entry.name}</SheetTitle>
              <SheetDescription className="mt-0.5">
                {isCreateMode
                  ? 'Enter your credentials to get started.'
                  : 'Update your credentials and settings.'}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <SheetBody className="space-y-4 py-6">
          {/* Status (edit mode only) */}
          {!isCreateMode && (
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Last synced</span>
                <span>
                  {integration.lastSyncAt
                    ? formatDistanceToNow(new Date(integration.lastSyncAt), { addSuffix: true })
                    : 'Never'}
                </span>
              </div>
            </div>
          )}

          {/* Credentials */}
          <div className="grid gap-2">
            <Label htmlFor="cred-username">Username</Label>
            <Input
              id="cred-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cred-password">Password</Label>
            <div className="flex gap-2">
              <Input
                id="cred-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isCreateMode ? 'Password' : 'Leave blank to keep existing'}
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
              />
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {entry.fields.map((field) => (
            <div key={field.key} className="grid gap-2">
              <Label htmlFor={field.key}>
                {field.label}
                {field.required && <span className="ml-1 text-destructive">*</span>}
              </Label>
              <Input
                id={field.key}
                type={field.type === 'number' ? 'number' : 'text'}
                value={configFields[field.key] ?? ''}
                onChange={(e) =>
                  setConfigFields((prev) => ({ ...prev, [field.key]: e.target.value }))
                }
                placeholder={field.placeholder}
              />
            </div>
          ))}

          {testMutation.data && (
            <p
              className={`text-sm ${testMutation.data.success ? 'text-green-600' : 'text-destructive'}`}
            >
              {testMutation.data.success
                ? 'Connection successful'
                : `Connection failed: ${testMutation.data.error ?? 'Unknown error'}`}
            </p>
          )}
        </SheetBody>

        <SheetActions
          start={
            <>
              {!isCreateMode && (
                <Button variant="outline" onClick={onDisconnect}>
                  Disconnect
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => testMutation.mutate()}
                disabled={!canTest || testMutation.isPending}
              >
                {testMutation.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                Test
              </Button>
            </>
          }
        >
          <SheetCancel />
          {isCreateMode ? (
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canCreate || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {createMutation.isPending ? 'Connecting...' : 'Connect'}
            </Button>
          ) : (
            <SaveButton
              type="button"
              onClick={() => saveMutation.mutate()}
              isPending={saveMutation.isPending}
            />
          )}
        </SheetActions>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// OAuth client-credentials Config Drawer (athenaOne)
// =============================================================================
// Client_id/secret live on the connector service in env vars (one app-level
// client per environment). The only per-org input is the `practiceId`, which
// goes into `integration.credentials` as plaintext JSONB.

function OAuthClientCredsConfigDrawer({
  open,
  onOpenChange,
  entry,
  integration,
  onDisconnect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: IntegrationCatalogEntry;
  integration: IntegrationResponse | null;
  onDisconnect: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const isCreateMode = integration === null;

  const [configFields, setConfigFields] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of entry.fields) initial[field.key] = '';
    return initial;
  });

  // Catalog fields live in `integration.config` (matches the OA/credentials-drawer
  // convention — practiceId/officeId are stable tenant IDs, not auth secrets).
  useEffect(() => {
    if (!open) return;
    const cfg = (integration?.config ?? {}) as Record<string, unknown>;
    const nextConfig: Record<string, string> = {};
    for (const field of entry.fields) {
      const v = cfg[field.key];
      nextConfig[field.key] = typeof v === 'string' ? v : '';
    }
    setConfigFields(nextConfig);
  }, [open, integration, entry.fields]);

  const allRequiredFilled = entry.fields
    .filter((f) => f.required)
    .every((f) => (configFields[f.key] ?? '').length > 0);

  const testMutation = useMutation({
    mutationFn: () =>
      getIntegrationClient().testCredentials({
        source: entry.type,
        credentials: {},
        config: configFields,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      getIntegrationClient().create({
        source: entry.type,
        credentials: {},
        config: configFields,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success(`${entry.name} connected`);
      onOpenChange(false);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      getIntegrationClient().update(integration!.id, {
        config: configFields,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success(`${entry.name} updated`);
    },
  });

  // `integration` is the row as it was when the drawer opened, and the drawer stays open
  // after Save. Prefer the row the save returned, so a changed practice ID shows the name
  // the API just looked up for it (ADR-260), not the previous practice's.
  const savedConfig = (saveMutation.data ?? integration)?.config;
  const savedAccountName =
    typeof savedConfig?.accountName === 'string' ? savedConfig.accountName : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <div className="flex items-center gap-3">
            {entry.logoUrl ? (
              <img src={entry.logoUrl} alt={entry.type} className="h-8 w-8 object-contain" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
                <Plug className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div>
              <SheetTitle>{isCreateMode ? `Connect ${entry.name}` : entry.name}</SheetTitle>
              <SheetDescription className="mt-0.5">
                {isCreateMode ? 'Enter your practice ID to connect.' : 'Update your practice ID.'}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <SheetBody className="space-y-4 py-6">
          {!isCreateMode && (
            <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
              {savedAccountName && (
                <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
                  <span>Practice</span>
                  <span className="truncate font-medium text-foreground" title={savedAccountName}>
                    {savedAccountName}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Last synced</span>
                <span>
                  {integration.lastSyncAt
                    ? formatDistanceToNow(new Date(integration.lastSyncAt), { addSuffix: true })
                    : 'Never'}
                </span>
              </div>
            </div>
          )}

          {entry.fields.map((field) => (
            <div key={field.key} className="grid gap-2">
              <Label htmlFor={field.key}>
                {field.label}
                {field.required && <span className="ml-1 text-destructive">*</span>}
              </Label>
              <Input
                id={field.key}
                type={field.type === 'number' ? 'number' : 'text'}
                value={configFields[field.key] ?? ''}
                onChange={(e) => {
                  setConfigFields((prev) => ({ ...prev, [field.key]: e.target.value }));
                  // A "Connected to <practice>" result belongs to the ID that was tested.
                  testMutation.reset();
                }}
                placeholder={field.placeholder}
              />
            </div>
          ))}

          <p className="text-xs text-muted-foreground">
            API client credentials are configured on the server. You only need to provide your
            practice ID.
          </p>

          {testMutation.data && (
            <p
              className={`text-sm ${testMutation.data.success ? 'text-green-600' : 'text-destructive'}`}
            >
              {testMutation.data.success
                ? testMutation.data.accountName
                  ? `Connected to ${testMutation.data.accountName}`
                  : 'Connection successful'
                : `Connection failed: ${testMutation.data.error ?? 'Unknown error'}`}
            </p>
          )}
        </SheetBody>

        <SheetActions
          start={
            <>
              {!isCreateMode && (
                <Button variant="outline" onClick={onDisconnect}>
                  Disconnect
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => testMutation.mutate()}
                disabled={!allRequiredFilled || testMutation.isPending}
              >
                {testMutation.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                Test
              </Button>
            </>
          }
        >
          <SheetCancel />
          {isCreateMode ? (
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!allRequiredFilled || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {createMutation.isPending ? 'Connecting...' : 'Connect'}
            </Button>
          ) : (
            <SaveButton
              type="button"
              onClick={() => saveMutation.mutate()}
              isPending={saveMutation.isPending}
            />
          )}
        </SheetActions>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// Disconnect Dialog
// =============================================================================

function DisconnectDialog({
  open,
  onOpenChange,
  integration,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: IntegrationResponse;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const catalogEntry = getCatalogEntry(integration.source);
  const displayName = catalogEntry?.name ?? integration.source;

  const deleteMutation = useMutation({
    mutationFn: () => getIntegrationClient().remove(integration.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success(`${displayName} disconnected`);
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Disconnect {displayName}</DialogTitle>
          <DialogDescription>
            This will stop syncing data from {displayName}. You can reconnect it later.
          </DialogDescription>
        </DialogHeader>

        {deleteMutation.isError && (
          <p className="text-sm text-destructive">Failed to disconnect integration.</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {deleteMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Sync Dialog (for EHR integrations)
// =============================================================================

function SyncDialog({
  open,
  onOpenChange,
  integration,
  displayName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: IntegrationResponse;
  displayName: string;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [syncProviders, setSyncProviders] = useState(true);
  const [syncPatients, setSyncPatients] = useState(true);
  const [syncAppointments, setSyncAppointments] = useState(true);
  const [syncSlots, setSyncSlots] = useState(true);

  const canSync = syncProviders || syncPatients || syncAppointments || syncSlots;

  const syncMutation = useMutation({
    mutationFn: (options: SyncOptions) => getIntegrationClient().sync(integration.id, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      toast.success('Sync started');
      onOpenChange(false);
    },
  });

  const handleSync = (): void => {
    syncMutation.mutate({ syncProviders, syncPatients, syncAppointments, syncSlots });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Sync {displayName}</DialogTitle>
          <DialogDescription>Choose what data to sync.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <label className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-muted/50">
            <Checkbox
              checked={syncProviders}
              onCheckedChange={(checked) => setSyncProviders(checked === true)}
            />
            <Stethoscope className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Providers</div>
              <div className="text-xs text-muted-foreground">
                Provider directory — names, credentials, specialties
              </div>
            </div>
          </label>

          <label className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-muted/50">
            <Checkbox
              checked={syncPatients}
              onCheckedChange={(checked) => setSyncPatients(checked === true)}
            />
            <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Patients</div>
              <div className="text-xs text-muted-foreground">
                Patient demographics and contact info
              </div>
            </div>
          </label>

          <label className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-muted/50">
            <Checkbox
              checked={syncAppointments}
              onCheckedChange={(checked) => setSyncAppointments(checked === true)}
            />
            <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Appointments</div>
              <div className="text-xs text-muted-foreground">
                Booked appointments from the calendar
              </div>
            </div>
          </label>

          <label className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-muted/50">
            <Checkbox
              checked={syncSlots}
              onCheckedChange={(checked) => setSyncSlots(checked === true)}
            />
            <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Available Slots</div>
              <div className="text-xs text-muted-foreground">Open time slots for scheduling</div>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSync} disabled={!canSync || syncMutation.isPending}>
            {syncMutation.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            )}
            Start Sync
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
